import { Box, IconButton, Stack, Tooltip, Typography, TypographyVariant } from "@mui/material";
import React, { ReactElement, SyntheticEvent, useState } from "react";
import { DuplicateIcon } from "src/foundations/icons";
import { SenecSnackbar } from "src/newsenec-ui-components";
import { Property } from "csstype";

export interface CopyToClipboardProps {
    /**
     * custom icon that replaces the default one
     * */
    customIcon?: ReactElement | undefined;
    /**
     * position of the icon. if "none" the icon is not shown
     * */
    iconPosition?: "left" | "right" | "none";
    /**
     * customisable default icon color
     * */
    defaultIconColor?: "inherit" | "disabled" | "action" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
    /**
     * tooltip customisable typography variant
     * */
    tooltipTextVariant?: TypographyVariant;
    /**
     * `Typography` element containing the text that can be copied to the clipboard
     * */
    child: ReactElement;
    /**
     * translated text showed in the tooltip after the click action
     * */
    notCopiedMsg?: string;
    /**
     * translated text showed in the tooltip when hovering before the click action
     * */
    copiedMsg?: string;
    /**
     * if true click on component shows a "copied" message in a toast. It's needed for mobile's copy action feedback
     */
    touchScreen?: boolean;
    /**
     * gets called after clicking on the trigger to run the copy command, includes the `MouseEvent` which triggered the copy, can be used to f. ex. run `event.stopPropagation()`
     */
    onCopy?: (event: React.MouseEvent) => void;
    /**
     * adjust horizontal placement of the text and icon. consider using "space-between" if required
     */
    justifyContent?: Property.JustifyContent;
}

/**
 * Component with text and (optionally) a custom or default icon that when clicked copies the content to the clipboard.
 * @returns React component capable of coping the text to the clip board when click on the icon or Typography text
 */
export const CopyToClipboard = ({
    customIcon,
    iconPosition = "right",
    child,
    defaultIconColor = "action",
    tooltipTextVariant = "body1",
    notCopiedMsg = "Copy text",
    copiedMsg = "Copied",
    touchScreen = false,
    onCopy,
    justifyContent = "flex-start",
}: CopyToClipboardProps) => {
    const [copied, setCopied] = useState(false);

    const handleCopyToClipboard = (val: string, event: React.MouseEvent) => {
        navigator.clipboard.writeText(val);
        setCopied(true);
        // Reset the copied state after 2 seconds to give user feedback
        setTimeout(() => setCopied(false), 2000);

        if (onCopy) {
            onCopy(event);
        }
    };

    return (
        <>
            {touchScreen ? (
                <MobileView
                    parentProps={{
                        customIcon,
                        iconPosition,
                        child,
                        defaultIconColor,
                        tooltipTextVariant,
                        notCopiedMsg,
                        copiedMsg,
                        touchScreen,
                        onCopy,
                        justifyContent,
                    }}
                    onCopyToClipboard={handleCopyToClipboard}
                />
            ) : (
                <DesktopView
                    parentProps={{
                        customIcon,
                        iconPosition,
                        child,
                        defaultIconColor,
                        tooltipTextVariant,
                        notCopiedMsg,
                        copiedMsg,
                        touchScreen,
                        onCopy,
                        justifyContent,
                    }}
                    copied={copied}
                    onCopyToClipboard={handleCopyToClipboard}
                />
            )}
        </>
    );
};

const getTextContent = (el: ReactElement | string | undefined): string => {
    if (!el) {
        return "";
    }
    if (typeof el === "string") {
        return el;
    }

    const children = el.props && el.props.children;
    if (children instanceof Array) {
        return children.map(getTextContent).join("\n");
    }
    return getTextContent(children);
};

interface ViewsProps {
    parentProps: CopyToClipboardProps;
    copied?: boolean;
    onCopyToClipboard: (val: string, event: React.MouseEvent) => void;
}

/**
 * It displays a tooltip when hovering on the icon or text. The tooltip message changed "copy" to "copied" on click
 */
const DesktopView = ({ parentProps, copied, onCopyToClipboard }: ViewsProps) => {
    return (
        <Stack
            direction={parentProps.iconPosition === "left" ? "row" : "row-reverse"}
            alignItems={"center"}
            justifyContent={parentProps.justifyContent}
            width={parentProps.justifyContent === "space-between" ? "100%" : "fit-content"}
        >
            <Box>
                <Tooltip
                    placement="top"
                    data-testid={"copy-tooltip"}
                    title={
                        <Typography variant={parentProps.tooltipTextVariant}>
                            {copied ? parentProps.copiedMsg : parentProps.notCopiedMsg}
                        </Typography>
                    }
                >
                    <Box>
                        <IconSection parentProps={parentProps} onCopyToClipboard={onCopyToClipboard} />
                    </Box>
                </Tooltip>
            </Box>
            <>{parentProps.child}</>
        </Stack>
    );
};

/**
 * It doesn't show a tooltip but a snackbar with a "copied" message
 */
const MobileView = ({ parentProps, onCopyToClipboard }: ViewsProps) => {
    const [showToast, setShowToast] = useState(false);

    const handleClick = (val: string, event: React.MouseEvent) => {
        onCopyToClipboard(val, event);
        setShowToast(true);
    };

    const handleToastClose = (_: SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") return;
        setShowToast(false);
    };

    return (
        <Box>
            <Stack
                direction={parentProps.iconPosition === "left" ? "row" : "row-reverse"}
                alignItems={"center"}
                justifyContent={parentProps.justifyContent}
                width={parentProps.justifyContent === "space-between" ? "100%" : "fit-content"}
            >
                <IconSection parentProps={parentProps} onCopyToClipboard={handleClick} />
                {parentProps.child}
            </Stack>
            <SenecSnackbar
                anchorOrigin={{
                    horizontal: "center",
                    vertical: "bottom",
                }}
                title={parentProps.copiedMsg ?? "Copied"}
                severity={"success"}
                open={showToast}
                onClose={handleToastClose}
                autoHideDuration={2000}
            />
        </Box>
    );
};

const IconSection = ({ parentProps, onCopyToClipboard }: ViewsProps) => {
    return (
        <Box sx={{ "&:hover": { cursor: "pointer" } }}>
            {parentProps.iconPosition !== "none" && (
                <IconButton
                    disableRipple
                    data-testid={"copy-icon"}
                    onClick={(event) => onCopyToClipboard(getTextContent(parentProps.child), event)}
                    sx={{
                        pl: parentProps.iconPosition === "left" ? "0" : "",
                        "&:hover, &.active, &.focus": { backgroundColor: "transparent" },
                    }}
                >
                    {parentProps.customIcon ? parentProps.customIcon : <DuplicateIcon color={parentProps.defaultIconColor} />}
                </IconButton>
            )}
        </Box>
    );
};
